import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calculator, 
  Plus, 
  Minus, 
  Star, 
  AlertTriangle, 
  Info, 
  Settings,
  Trash2,
  Edit,
  Check,
  X
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Textarea } from '../../ui/textarea';
import { Separator } from '../../ui/separator';
import { Slider } from '../../ui/slider';

interface ScoringRule {
  id: string;
  type: 'partial_credit' | 'bonus' | 'penalty' | 'time_bonus' | 'attempt_penalty';
  name: string;
  description: string;
  value: number;
  condition?: string;
  isActive: boolean;
}

interface CustomScoringProps {
  basePoints: number;
  onPointsChange: (points: number) => void;
  questionType: string;
  scoringRules?: ScoringRule[];
  onScoringRulesChange: (rules: ScoringRule[]) => void;
}

const CustomScoring: React.FC<CustomScoringProps> = ({
  basePoints,
  onPointsChange,
  questionType,
  scoringRules = [],
  onScoringRulesChange
}) => {
  const [isAdvancedMode, setIsAdvancedMode] = useState(scoringRules.length > 0);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [editingRule, setEditingRule] = useState<ScoringRule | null>(null);
  const [newRule, setNewRule] = useState<Partial<ScoringRule>>({
    type: 'partial_credit',
    name: '',
    description: '',
    value: 0.5,
    isActive: true
  });

  const ruleTypes = [
    {
      value: 'partial_credit',
      label: 'Crédit partiel',
      description: 'Points partiels pour réponses partiellement correctes',
      icon: <Calculator className="w-4 h-4" />,
      color: 'blue'
    },
    {
      value: 'bonus',
      label: 'Bonus',
      description: 'Points bonus pour performance exceptionnelle',
      icon: <Star className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      value: 'penalty',
      label: 'Pénalité',
      description: 'Pénalité pour réponses incorrectes',
      icon: <Minus className="w-4 h-4" />,
      color: 'red'
    },
    {
      value: 'time_bonus',
      label: 'Bonus temps',
      description: 'Bonus basé sur la rapidité de réponse',
      icon: <Plus className="w-4 h-4" />,
      color: 'green'
    },
    {
      value: 'attempt_penalty',
      label: 'Pénalité tentatives',
      description: 'Pénalité pour tentatives multiples',
      icon: <AlertTriangle className="w-4 h-4" />,
      color: 'orange'
    }
  ];

  const getRuleTypeInfo = (type: string) => {
    return ruleTypes.find(rt => rt.value === type) || ruleTypes[0];
  };

  const calculateTotalPoints = () => {
    let total = basePoints;
    
    scoringRules.forEach(rule => {
      if (!rule.isActive) return;
      
      switch (rule.type) {
        case 'bonus':
        case 'time_bonus':
          total += rule.value;
          break;
        case 'penalty':
        case 'attempt_penalty':
          total -= Math.abs(rule.value);
          break;
        case 'partial_credit':
          // Le crédit partiel ne change pas le total maximum, mais affecte la notation
          break;
      }
    });
    
    return Math.max(0, total);
  };

  const addRule = () => {
    if (!newRule.name || !newRule.description) return;

    const rule: ScoringRule = {
      id: Date.now().toString(),
      type: newRule.type as ScoringRule['type'],
      name: newRule.name,
      description: newRule.description,
      value: newRule.value || 0,
      condition: newRule.condition,
      isActive: true
    };

    onScoringRulesChange([...scoringRules, rule]);
    setNewRule({
      type: 'partial_credit',
      name: '',
      description: '',
      value: 0.5,
      isActive: true
    });
    setIsAddingRule(false);
  };

  const updateRule = (ruleId: string, updates: Partial<ScoringRule>) => {
    const updatedRules = scoringRules.map(rule =>
      rule.id === ruleId ? { ...rule, ...updates } : rule
    );
    onScoringRulesChange(updatedRules);
  };

  const deleteRule = (ruleId: string) => {
    const updatedRules = scoringRules.filter(rule => rule.id !== ruleId);
    onScoringRulesChange(updatedRules);
  };

  const getDefaultRulesForQuestionType = () => {
    const defaults: Partial<ScoringRule>[] = [];
    
    switch (questionType) {
      case 'multiple_choice':
        defaults.push({
          type: 'partial_credit',
          name: 'Crédit partiel',
          description: 'Points partiels basés sur le pourcentage de bonnes réponses sélectionnées',
          value: 0.5
        });
        defaults.push({
          type: 'penalty',
          name: 'Pénalité mauvaises réponses',
          description: 'Pénalité pour chaque mauvaise réponse sélectionnée',
          value: 0.25
        });
        break;
      case 'single_choice':
        defaults.push({
          type: 'time_bonus',
          name: 'Bonus rapidité',
          description: 'Bonus si réponse donnée en moins de 30 secondes',
          value: 0.5
        });
        break;
      case 'text_input':
        defaults.push({
          type: 'partial_credit',
          name: 'Évaluation manuelle',
          description: 'Points partiels basés sur l\'évaluation manuelle',
          value: 0.75
        });
        break;
    }
    
    return defaults;
  };

  const applyDefaultRules = () => {
    const defaults = getDefaultRulesForQuestionType();
    const newRules = defaults.map((rule, index) => ({
      id: `default_${Date.now()}_${index}`,
      type: rule.type as ScoringRule['type'],
      name: rule.name!,
      description: rule.description!,
      value: rule.value!,
      isActive: true
    }));
    
    onScoringRulesChange([...scoringRules, ...newRules]);
  };

  return (
    <div className="space-y-4">
      {/* Points de base */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-300">Points de base</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={basePoints}
            onChange={(e) => onPointsChange(parseFloat(e.target.value) || 0)}
            className="bg-gray-700 border-gray-600 text-white"
          />
        </div>
        <div>
          <Label className="text-gray-300">Points maximum</Label>
          <div className="bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white">
            {calculateTotalPoints().toFixed(1)}
          </div>
        </div>
      </div>

      {/* Mode avancé */}
      <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
        <div className="flex items-center space-x-3">
          <Settings className="w-5 h-5 text-blue-400" />
          <div>
            <Label className="text-white">Notation avancée</Label>
            <p className="text-xs text-gray-400">
              Système de notation personnalisé avec règles complexes
            </p>
          </div>
        </div>
        <Switch
          checked={isAdvancedMode}
          onCheckedChange={(checked) => {
            setIsAdvancedMode(checked);
            if (!checked) {
              onScoringRulesChange([]);
            }
          }}
        />
      </div>

      {/* Règles de notation avancées */}
      <AnimatePresence>
        {isAdvancedMode && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4"
          >
            <Card className="bg-gray-800/50 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-sm flex items-center justify-between">
                  <span>Règles de notation</span>
                  <div className="flex items-center space-x-2">
                    {getDefaultRulesForQuestionType().length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={applyDefaultRules}
                        className="border-green-500/30 text-green-400 hover:bg-green-500/10 h-7 text-xs"
                      >
                        Règles suggérées
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAddingRule(true)}
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 h-7"
                    >
                      <Plus size={12} className="mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {scoringRules.length === 0 ? (
                  <div className="text-center py-6 text-gray-400">
                    <Calculator className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Aucune règle de notation personnalisée</p>
                    <p className="text-xs">Cliquez sur "Ajouter" pour créer une règle</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {scoringRules.map((rule, index) => {
                      const ruleInfo = getRuleTypeInfo(rule.type);
                      return (
                        <motion.div
                          key={rule.id}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`p-3 rounded-lg border transition-colors ${
                            rule.isActive 
                              ? 'bg-gray-700/30 border-gray-600/30' 
                              : 'bg-gray-700/10 border-gray-600/10 opacity-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${ruleInfo.color}-500/20`}>
                                {ruleInfo.icon}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-white text-sm font-medium">{rule.name}</span>
                                  <Badge variant="outline" className={`border-${ruleInfo.color}-500/30 text-${ruleInfo.color}-400 bg-${ruleInfo.color}-500/10 text-xs`}>
                                    {ruleInfo.label}
                                  </Badge>
                                  <Badge variant="outline" className="text-xs">
                                    {rule.value > 0 ? '+' : ''}{rule.value} pts
                                  </Badge>
                                </div>
                                <p className="text-gray-400 text-xs">{rule.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch
                                checked={rule.isActive}
                                onCheckedChange={(checked) => updateRule(rule.id, { isActive: checked })}
                                size="sm"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingRule(rule)}
                                className="text-gray-400 hover:text-white h-7 w-7 p-0"
                              >
                                <Edit size={12} />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteRule(rule.id)}
                                className="text-red-400 hover:text-red-300 h-7 w-7 p-0"
                              >
                                <Trash2 size={12} />
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}

                {/* Formulaire d'ajout de règle */}
                <AnimatePresence>
                  {isAddingRule && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-gray-600 pt-4 mt-4"
                    >
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-gray-300 text-xs">Type de règle</Label>
                            <Select
                              value={newRule.type}
                              onValueChange={(value) => setNewRule(prev => ({ ...prev, type: value as ScoringRule['type'] }))}
                            >
                              <SelectTrigger className="bg-gray-700 border-gray-600 text-white h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {ruleTypes.map(type => (
                                  <SelectItem key={type.value} value={type.value}>
                                    <div className="flex items-center space-x-2">
                                      {type.icon}
                                      <span>{type.label}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-gray-300 text-xs">Valeur (points)</Label>
                            <Input
                              type="number"
                              step="0.1"
                              value={newRule.value}
                              onChange={(e) => setNewRule(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                              className="bg-gray-700 border-gray-600 text-white h-8"
                              placeholder="0.5"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-gray-300 text-xs">Nom de la règle</Label>
                          <Input
                            value={newRule.name}
                            onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
                            className="bg-gray-700 border-gray-600 text-white h-8"
                            placeholder="Ex: Bonus rapidité"
                          />
                        </div>
                        <div>
                          <Label className="text-gray-300 text-xs">Description</Label>
                          <Textarea
                            value={newRule.description}
                            onChange={(e) => setNewRule(prev => ({ ...prev, description: e.target.value }))}
                            className="bg-gray-700 border-gray-600 text-white resize-none"
                            rows={2}
                            placeholder="Description de la règle de notation..."
                          />
                        </div>
                        <div className="flex items-center space-x-2 pt-2">
                          <Button
                            size="sm"
                            onClick={addRule}
                            disabled={!newRule.name || !newRule.description}
                            className="bg-blue-600 hover:bg-blue-700 h-7"
                          >
                            <Check size={12} className="mr-1" />
                            Ajouter
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setIsAddingRule(false);
                              setNewRule({
                                type: 'partial_credit',
                                name: '',
                                description: '',
                                value: 0.5,
                                isActive: true
                              });
                            }}
                            className="h-7"
                          >
                            <X size={12} className="mr-1" />
                            Annuler
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>

            {/* Aperçu du système de notation */}
            {scoringRules.length > 0 && (
              <Card className="bg-blue-500/10 border-blue-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-blue-400 text-sm flex items-center">
                    <Info className="w-4 h-4 mr-2" />
                    Aperçu du système de notation
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-xs">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Points de base:</span>
                      <span className="text-white">{basePoints} pts</span>
                    </div>
                    {scoringRules.filter(r => r.isActive).map(rule => (
                      <div key={rule.id} className="flex justify-between">
                        <span className="text-gray-300">{rule.name}:</span>
                        <span className={`${rule.value >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {rule.value > 0 ? '+' : ''}{rule.value} pts
                        </span>
                      </div>
                    ))}
                    <Separator className="bg-gray-600" />
                    <div className="flex justify-between font-medium">
                      <span className="text-white">Total maximum:</span>
                      <span className="text-white">{calculateTotalPoints().toFixed(1)} pts</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export { CustomScoring };
